import { CartItemsTray } from '@app/components/CartItemsTray';
import { availableLocales } from '@app/constants';
import {
  CartAndDeliveryDatesDataProvider,
  CartCheckoutClient,
} from '@app/data/clients/cartCheckoutClient';
import { usePrefersReducedMotion } from '@app/hooks/usePrefersReducedMotion';
import { withAppServerSideProps } from '@app/lib/withAppServerSideProps';
import Box from '@redbubble/design-system/src/Box';
import * as constants from '@redbubble/design-system/src/constants';
import Image from '@redbubble/design-system/src/Image';
import PageSection from '@redbubble/design-system/src/PageSection';
import Text from '@redbubble/design-system/src/Text';
import { useViewportWidth } from '@redbubble/next/hooks/use-viewport-width';
import { defineMessages, FormattedNumber, useIntl } from '@redbubble/next/intl';
import { AppProps, DeviceType, UserInfo } from '@redbubble/next/types';
import { AnalyticsPageType } from '@redbubble/next-analytics/gtm';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import {
  RSPageType,
  useRSPageView,
} from '@redbubble/next-provider/rudderstack';
import { OpenGraph, Seo } from '@redbubble/next-seo';
import type { NextPage } from 'next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Confetti from 'react-dom-confetti';
import { trackGA4AddGiftCardToCartEvent } from '../cart/utils/analytics';
import { DD_PDP_ERROR_METRIC_NAME } from '../i/constants';
import { GiftCardForm } from './components/GiftCardForm/GiftCardForm';
import { HowItWorks } from './components/HowItWorks/HowItWorks';
import { DEFAULT_VALUE } from './constants';
import giftCardImage from './gift-card.png';
import styles from './NewGiftCardPage.module.scss';

const { publicRuntimeConfig } = getConfig();
const { baseUrl } = publicRuntimeConfig.app;

const messages = defineMessages({
  heading: {
    defaultMessage: 'Redbubble Gift Cards',
  },
  title: {
    defaultMessage: 'Gift Cards | Redbubble',
  },
  description: {
    defaultMessage:
      'Finding the perfect gift isn’t easy. Why not put the choice in their hands? Give a Redbubble digital gift card and let them choose exactly the thing they love.',
  },
});

const cartCheckoutClient = new CartCheckoutClient({
  logging: {
    metricName: DD_PDP_ERROR_METRIC_NAME,
    context: 'GiftCardPage',
  },
});

export const NewGiftCardPage: NextPage = () => {
  useRSPageView(RSPageType.GiftCard);
  const intl = useIntl();
  const router = useRouter();
  const [{ currency }] = useUserInfo<UserInfo>();

  const { deviceType } = usePageProps<AppProps>();
  const { isOrAbove } = useViewportWidth(deviceType);
  const isDesktop = isOrAbove(DeviceType.Desktop);

  const [giftCardValue, setGiftCardValue] = useState(DEFAULT_VALUE);
  const handleGiftCardAmountChange = ({ values }) => {
    const amount =
      values.amount === 'other' ? values.otherAmount : values.amount;
    setGiftCardValue(amount);
  };

  const [atcDrawerOpen, setAtcDrawerOpen] = useState<boolean>(false);
  const [confettiActive, setConfettiActive] = useState<boolean>(false);
  const [reducedMotion, setReducedMotion] = useState<boolean>(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    setReducedMotion(prefersReducedMotion);
  }, [prefersReducedMotion]);

  const canonicalUrl = `${baseUrl}${router.asPath}`;
  const openGraph: OpenGraph = {
    type: 'website',
    url: canonicalUrl,
  };

  const languageAlternates = Object.values(availableLocales).map(
    ({ name, urlSegment }) => {
      return {
        hrefLang: name,
        href: `${baseUrl}${urlSegment}${router.asPath}`,
      };
    }
  );

  return (
    <Box element="main">
      <CartAndDeliveryDatesDataProvider client={cartCheckoutClient}>
        {!reducedMotion && (
          <Box className={styles.confetti} data-testid="confetti-box">
            <Confetti
              active={confettiActive}
              config={{
                colors: ['#D92D02', '#FA8F0F', '#ADECF0', '#51BA88'],
                angle: 270,
                spread: isDesktop ? 290 : 129,
                startVelocity: isDesktop ? 70 : 30,
                elementCount: isDesktop ? 300 : 150,
                duration: isDesktop ? 1400 : 2800,
              }}
            />
          </Box>
        )}
        <CartItemsTray
          open={atcDrawerOpen}
          onCloseRequested={() => {
            setAtcDrawerOpen(false);
            setConfettiActive(false);
          }}
        />
        <Seo
          title={intl.formatMessage(messages.title)}
          description={intl.formatMessage(messages.description)}
          openGraph={openGraph}
          languageAlternates={languageAlternates}
          canonical={canonicalUrl}
        />
        <PageSection
          size={constants.SMALL}
          padding={isDesktop ? 2 : 1}
          profile={isDesktop ? constants.DESKTOP : constants.MOBILE}
        >
          <Box className={styles.headingArea} paddingTop="xl" marginBottom="xl">
            <Text display="block" type="display2">
              {intl.formatMessage(messages.heading)}
            </Text>
            <Box marginBottom="xl" />
            <Text display="block" type="display4">
              {intl.formatMessage(messages.description)}
            </Text>
          </Box>

          <Box className={styles.selectCardSection} paddingTop="none">
            <Box
              justifyContent="center"
              display="flex"
              className={styles.giftCard}
            >
              <Box className={styles.cardContainer}>
                <Image src={giftCardImage.src} fluid alt="Gift Card" />
                <Box padding="m" className={styles.currency}>
                  <Text
                    className={styles.selectedAmount}
                    data-testid="selected-amount"
                  >
                    <FormattedNumber
                      value={giftCardValue}
                      style="currency"
                      currency={currency}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </Text>
                </Box>
              </Box>
            </Box>
            <Box flex="1" paddingTop="m">
              <GiftCardForm
                onChange={handleGiftCardAmountChange}
                onAddToCart={() => {
                  trackGA4AddGiftCardToCartEvent({
                    giftCardAmount: giftCardValue,
                    currency,
                    pageType: AnalyticsPageType.GiftCard,
                  });
                  setAtcDrawerOpen(true);
                  setConfettiActive(true);
                }}
              />
            </Box>
          </Box>
        </PageSection>
        <PageSection size={constants.SMALL} paddingTop="xl" paddingBottom="xl">
          <HowItWorks isDesktop={isDesktop} />
        </PageSection>
      </CartAndDeliveryDatesDataProvider>
    </Box>
  );
};

export const getServerSideProps = withAppServerSideProps();

export default NewGiftCardPage;
